import { useMemo } from 'react';

import { IMyRegistration } from 'src/interfaces/Registration';

export interface RegistrationEnrichment {
  competitionId: string;
  distanceId: string;
  price: number;
  status: IMyRegistration['status'];
  createdAt?: string | Date;
}

interface UseMyRegistrationsMapsOptions {
  registrations: IMyRegistration[];
  editionId: string | null | undefined;
  /** When true, only registrations for editionId are used for enrichment (applicants view). */
  strictEdition?: boolean;
}

export function useMyRegistrationsMaps({
  registrations,
  editionId,
  strictEdition = false,
}: UseMyRegistrationsMapsOptions) {
  const registrationIdsByParticipantId = useMemo(() => {
    const map: Record<string, string> = {};
    registrations.forEach(r => {
      if (!r.participantId) {
        return;
      }
      if (strictEdition) {
        if (r.editionId === editionId) {
          map[r.participantId] = r.id;
        }
      } else if (editionId && r.editionId === editionId) {
        map[r.participantId] = r.id;
      }
    });
    return map;
  }, [registrations, editionId, strictEdition]);

  const enrichmentByParticipantId = useMemo(() => {
    const map: Record<string, RegistrationEnrichment> = {};

    if (strictEdition) {
      registrations.forEach(r => {
        if (r.participantId && r.editionId === editionId) {
          map[r.participantId] = {
            competitionId: r.competitionId,
            distanceId: r.distanceId,
            price: r.price,
            status: r.status,
            createdAt: r.createdAt,
          };
        }
      });
      return map;
    }

    const regsByParticipant: Record<string, IMyRegistration[]> = {};
    registrations.forEach(r => {
      if (r.participantId) {
        const bucket = regsByParticipant[r.participantId] ?? [];
        bucket.push(r);
        regsByParticipant[r.participantId] = bucket;
      }
    });

    Object.keys(regsByParticipant).forEach(participantId => {
      const participantRegs = regsByParticipant[participantId];
      if (!participantRegs) return;
      let selectedReg: IMyRegistration | undefined;

      if (editionId) {
        selectedReg = participantRegs.find(r => r.editionId === editionId);
      }

      if (!selectedReg && participantRegs.length > 0) {
        selectedReg = participantRegs.reduce((latest, current) => {
          const currentDate = current.createdAt ? new Date(current.createdAt).getTime() : 0;
          const latestDate = latest?.createdAt ? new Date(latest.createdAt).getTime() : 0;
          return currentDate > latestDate ? current : latest;
        });
      }

      if (selectedReg) {
        map[participantId] = {
          competitionId: selectedReg.competitionId,
          distanceId: selectedReg.distanceId,
          price: selectedReg.price,
          status: selectedReg.status,
          createdAt: selectedReg.createdAt,
        };
      }
    });

    return map;
  }, [registrations, editionId, strictEdition]);

  return {
    registrationIdsByParticipantId,
    enrichmentByParticipantId,
  };
}
